import type React from 'react';

/**
 * Shared tokens for the floating action dock (extracted 1:1 from the /cv
 * bottom bar — see components/cv/CVBottomBar[.web].tsx history). Values are
 * exact Figma exports; do not tweak them per-surface.
 */

// Figma brand-purple gradient for the progress arc + Enhance sparkle (node 3062-6178).
export const RING_PROGRESS_TOP = '#9D7FFE';
export const RING_PROGRESS_END = '#6035ED';

// Exact bar surface tokens from Figma (3147:710).
export const BAR_GRADIENT_TOP = '#F3F3F3';
export const BAR_GRADIENT_BOTTOM = '#FFFFFF';
export const BAR_BORDER_LIGHT = '#E9E9E9';
export const BAR_BORDER_DARK = 'rgba(255,255,255,0.08)';

/** Exact Figma stroke color for the dock glyphs and labels (mirrors CV_BAR_ICON_COLOR). */
export const DOCK_ICON_COLOR = '#7D7C81';

// Exact 4-layer drop shadow exported from Figma (3147:710 / 3062:6277) — web only.
export const BAR_SHADOW =
  '0px 7px 7px rgba(0,0,0,0.26), 0px 26px 13px rgba(0,0,0,0.04), 0px 59px 17.5px rgba(0,0,0,0.02)';
export const RING_SHADOW =
  '0px 7px 14px rgba(0,0,0,0.26), 0px 26px 26px rgba(0,0,0,0.04), 0px 59px 35px rgba(0,0,0,0.02)';
export const DARK_BAR_SHADOW =
  '0px 14px 34px rgba(0,0,0,0.62), 0px 2px 8px rgba(0,0,0,0.38), 0 0 0 1px rgba(255,255,255,0.04)';
export const DARK_RING_SHADOW =
  '0px 14px 34px rgba(0,0,0,0.62), 0px 2px 8px rgba(0,0,0,0.38), 0 0 0 1px rgba(255,255,255,0.05)';

// Glossy 3D sparkle used for the Enhance action (replaces the flat gradient SVG).
export const ENHANCE_ICON = require('../../../../assets/branding/upgrade-spark.png');

export interface ActionDockRingConfig {
  completionPercent: number;
  /** Small caption under the percent value, e.g. "set up". */
  label: string;
  a11yLabel?: string;
  a11yHint?: string;
  onPress: () => void;
}

/** Where the CV Health report stands; the launcher exposes it as `data-state` on web. */
export type ActionDockHealthState =
  | 'idle'
  | 'checking'
  | 'suggestions'
  | 'good'
  | 'stale';

/**
 * The CV Health launcher that takes the ring slot on the CV editor dock
 * (components/cv/CVBottomBar): the design's Heart Agent, a 48 px circle
 * holding the Health heart whose signal line is coloured by the score band.
 * No label, no count and never the number itself: the accessibility label
 * says the score and the sheet shows it.
 */
export interface ActionDockHealthConfig {
  state: ActionDockHealthState;
  /**
   * The settled score (0..100) that tints the signal line, or null while
   * there is none to show: no report, a running check, a stale or failed
   * report. Null draws the grey line.
   */
  score: number | null;
  onPress: () => void;
  /** "CV Health, score 72 out of 100. Open review." or the unavailable form. */
  a11yLabel: string;
  a11yHint?: string;
}

export interface ActionDockItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  onPress: () => void;
}

export interface ActionDockEnhanceConfig {
  /** Label of the trigger button that morphs the bar into the prompt composer. */
  label: string;
  defaultPrompt: string;
  submitA11yLabel?: string;
  onSubmit: (prompt: string) => void;
}

export interface ActionDockProps {
  /**
   * Web: `fixed` centers in the viewport, `content` centers inside the nearest
   * positioned content pane. Native always uses the screen-level absolute
   * placement, but accepts the prop for shared imports.
   */
  placement?: 'fixed' | 'content';
  ring?: ActionDockRingConfig;
  /**
   * The CV Health control, drawn in the ring slot. Only used when `ring` is
   * not set: a dock that passes `ring` (Company Page, or the CV editor while
   * Health is off) keeps its ring unchanged.
   */
  health?: ActionDockHealthConfig;
  items: ActionDockItem[];
  enhance?: ActionDockEnhanceConfig;
}
